// ╔════════════════════════════════════════════════════════════════════════════╗
// ║  AUTHSCREEN.JSX - VERSION: 2025.01.15.1000                                 ║
// ╚════════════════════════════════════════════════════════════════════════════╝
// ============================================================================
// 📦 VERSION: 2025.01.15.1000
// ============================================================================
// TRAK Quiz Game - Auth Component (Login/Register with Security Questions)
// ============================================================================
// CHANGELOG:
// 2025.01.15.1000 - Added 2-step registration with security questions
// 2025.01.15.1000 - Added forgot password flow with security questions (no email)
// 2025.01.15.1000 - Added lockout display after 3 failed attempts
// 2025.12.19.1630 - Added "Return to Game" link on logged-in profile view
// 2025.12.19.1545 - Added Forgot Password link and form on login screen
// 2025.12.12.1000 - Initial version with phone input
// ============================================================================
import React, { useState } from 'react';
import { login, register, isLoggedIn, logout } from '../api';
import PhoneInput from './PhoneInput';

const API_URL = 'https://api.traktag.com/api';

const AuthScreen = ({ onAuthSuccess, playerData, initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode); // 'login' or 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phoneData, setPhoneData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // 🆕 2025.01.15: Two-step registration
  const [registerStep, setRegisterStep] = useState(1); // 1 = basic info, 2 = security questions
  const [securityQ1, setSecurityQ1] = useState('');
  const [securityA1, setSecurityA1] = useState('');
  const [securityQ2, setSecurityQ2] = useState('');
  const [securityA2, setSecurityA2] = useState('');
  
  // 🆕 2025.01.15: Forgot Password with security questions
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1 = enter email/phone, 2 = answer questions, 3 = new password, 4 = success
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotPlayerId, setForgotPlayerId] = useState(null);
  const [forgotQuestion1, setForgotQuestion1] = useState('');
  const [forgotQuestion2, setForgotQuestion2] = useState('');
  const [forgotAnswer1, setForgotAnswer1] = useState('');
  const [forgotAnswer2, setForgotAnswer2] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [isLockedOut, setIsLockedOut] = useState(false);

  // Update mode when initialMode prop changes
  React.useEffect(() => {
    setMode(initialMode);
    setShowForgotPassword(false);
    setRegisterStep(1);
  }, [initialMode]);
  
  // Reset registration form when switching modes
  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    setRegisterStep(1);
    setError('');
    setSecurityQ1('');
    setSecurityA1('');
    setSecurityQ2('');
    setSecurityA2('');
  };
  
  // 🆕 Handle step 1 -> step 2 in registration
  const handleRegisterStep1 = (e) => {
    e.preventDefault();
    setError('');
    
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    // Move to step 2 - security questions
    setRegisterStep(2);
  };
  
  // 🆕 Handle final registration with security questions
  const handleRegisterStep2 = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!securityQ1.trim() || !securityA1.trim()) {
      setError('Please enter security question 1 and answer');
      return;
    }
    if (!securityQ2.trim() || !securityA2.trim()) {
      setError('Please enter security question 2 and answer');
      return;
    }
    
    setLoading(true);
    
    try {
      // Call register with security questions
      const result = await register(email, password, name, phoneData, {
        security_question_1: securityQ1.trim(),
        security_answer_1: securityA1.trim(),
        security_question_2: securityQ2.trim(),
        security_answer_2: securityA2.trim()
      });
      
      if (result.error) {
        setError(result.error);
      } else if (result.player) {
        // Check for pal referral bonus after successful registration
        const palCode = localStorage.getItem('palReferralCode');
        const palBonus = localStorage.getItem('palBonusAmount');
        
        if (palCode) {
          try {
            const token = localStorage.getItem('trak_token');
            const response = await fetch(`${API_URL}/pal/claim-referral`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                code: palCode,
                bonus_amount: parseInt(palBonus) || 50
              })
            });
            
            const data = await response.json();
            if (data.success) {
              console.log('✅ Referral bonus claimed:', data.bonus_traks);
              result.player.total_traks = (result.player.total_traks || 0) + data.bonus_traks;
            }
          } catch (err) {
            console.error('Failed to claim referral bonus:', err);
          }
          
          localStorage.removeItem('palReferralCode');
          localStorage.removeItem('palBonusAmount');
          localStorage.removeItem('palHelperName');
        }
        
        onAuthSuccess(result.player);
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }
    
    setLoading(false);
  };
  
  // 🆕 Forgot Password Step 1: Get security questions
  const handleForgotStep1 = async (e) => {
    e.preventDefault();
    setForgotError('');
    
    if (!forgotIdentifier.trim()) {
      setForgotError('Please enter your email or phone number');
      return;
    }
    
    setForgotLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/auth/get-security-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: forgotIdentifier.trim() })
      });
      
      const data = await response.json();
      
      if (data.locked) {
        setIsLockedOut(true);
        setForgotError(data.error);
      } else if (!response.ok) {
        setForgotError(data.error || 'Please check your details and try again');
      } else {
        setForgotPlayerId(data.player_id);
        setForgotQuestion1(data.question_1);
        setForgotQuestion2(data.question_2);
        setForgotStep(2);
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setForgotError('Connection failed. Please try again.');
    }
    
    setForgotLoading(false);
  };
  
  // 🆕 Forgot Password Step 2: Verify answer and set new password
  const handleForgotStep2 = async (e) => {
    e.preventDefault();
    setForgotError('');
    
    if (!forgotAnswer1.trim() && !forgotAnswer2.trim()) {
      setForgotError('Please answer at least one security question');
      return;
    }
    
    if (!forgotNewPassword || forgotNewPassword.length < 6) {
      setForgotError('New password must be at least 6 characters');
      return;
    }
    
    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotError('Passwords do not match');
      return;
    }
    
    setForgotLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/auth/verify-security-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player_id: forgotPlayerId,
          answer_1: forgotAnswer1.trim(),
          answer_2: forgotAnswer2.trim(),
          new_password: forgotNewPassword
        })
      });
      
      const data = await response.json();
      
      if (data.locked) {
        setIsLockedOut(true);
        setForgotError(data.error);
      } else if (!response.ok) {
        setForgotError(data.error || 'Verification failed');
      } else {
        // Success!
        setForgotStep(3);
      }
    } catch (err) {
      console.error('Verify security error:', err);
      setForgotError('Connection failed. Please try again.');
    }
    
    setForgotLoading(false);
  };
  
  // Reset forgot password form
  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotStep(1);
    setForgotIdentifier('');
    setForgotPlayerId(null);
    setForgotQuestion1('');
    setForgotQuestion2('');
    setForgotAnswer1('');
    setForgotAnswer2('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setForgotError('');
    setIsLockedOut(false);
  };

  // If already logged in, show profile
  if (isLoggedIn() && playerData) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full mx-auto mb-4 flex items-center justify-center">
              <span className="text-3xl">👤</span>
            </div>
            <h2 className="text-white text-xl font-bold">{playerData.name}</h2>
            <p className="text-gray-400 text-sm">{playerData.email}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-700 rounded-lg p-3 text-center">
              <p className="text-yellow-400 text-2xl font-bold">{(playerData.stats?.totalTraks || playerData.total_traks || 0).toLocaleString()}</p>
              <p className="text-gray-400 text-xs">TRAKs</p>
            </div>
            <div className="bg-gray-700 rounded-lg p-3 text-center">
              <p className="text-orange-400 text-2xl font-bold">{playerData.stats?.currentStreak || playerData.current_streak || 0}</p>
              <p className="text-gray-400 text-xs">Day Streak</p>
            </div>
          </div>
          
          <button
            onClick={() => {
              logout();
              onAuthSuccess(null);
            }}
            className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-3 rounded-lg"
          >
            Logout
          </button>
          
          {/* Return to Game link */}
          <button
            onClick={() => onAuthSuccess(playerData)}
            className="w-full mt-3 text-yellow-400 hover:text-yellow-300 text-sm font-medium"
          >
            Return to Game
          </button>
        </div>
      </div>
    );
  }

  // Handle login submit
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.error) {
        setError(result.error);
      } else if (result.player) {
        onAuthSuccess(result.player);
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-sm">
        {/* Logo/Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-black">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500">TRAK</span>
            <span className="text-white">tag</span>
          </h1>
          <p className="text-gray-400 text-sm mt-1">Quiz Game</p>
        </div>

        {/* 🆕 FORGOT PASSWORD FLOW */}
        {showForgotPassword ? (
          <div>
            {/* Locked Out Message */}
            {isLockedOut ? (
              <div className="text-center">
                <div className="text-5xl mb-4">🔒</div>
                <h2 className="text-white text-xl font-bold mb-2">Account Locked</h2>
                <p className="text-gray-400 mb-6">Too many failed attempts. Please try again in 15 minutes.</p>
                <button
                  onClick={resetForgotPassword}
                  className="w-full bg-gray-700 text-white font-bold py-3 rounded-lg"
                >
                  Back to Login
                </button>
              </div>
            ) : forgotStep === 1 ? (
              /* Step 1: Enter email or phone */
              <div>
                <div className="text-center mb-6">
                  <div className="text-4xl mb-2">🔐</div>
                  <h2 className="text-white text-xl font-bold">Reset Password</h2>
                  <p className="text-gray-400 text-sm mt-1">Enter your email or phone number to continue</p>
                </div>
                
                {forgotError && (
                  <div className="bg-red-600/20 border border-red-500 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
                    {forgotError}
                  </div>
                )}
                
                <form onSubmit={handleForgotStep1}>
                  <div className="mb-4">
                    <label className="text-gray-400 text-sm block mb-1">Email or Phone Number</label>
                    <input
                      type="text"
                      value={forgotIdentifier}
                      onChange={(e) => setForgotIdentifier(e.target.value)}
                      placeholder="your@email.com or 07123456789"
                      className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className={`w-full font-bold py-3 rounded-lg transition mb-3 ${
                      forgotLoading
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-500 to-teal-500 hover:from-blue-400 hover:to-teal-400 text-white'
                    }`}
                  >
                    {forgotLoading ? 'Loading...' : 'Continue →'}
                  </button>
                </form>
                
                <button
                  onClick={resetForgotPassword}
                  className="w-full text-gray-400 hover:text-white text-sm py-2"
                >
                  ← Back to Login
                </button>
              </div>
            ) : forgotStep === 2 ? (
              /* Step 2: Answer security questions */
              <div>
                <div className="text-center mb-4">
                  <div className="text-4xl mb-2">❓</div>
                  <h2 className="text-white text-xl font-bold">Security Questions</h2>
                  <p className="text-gray-400 text-sm mt-1">Answer at least ONE question correctly</p>
                </div>
                
                {forgotError && (
                  <div className="bg-red-600/20 border border-red-500 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
                    {forgotError}
                  </div>
                )}
                
                <form onSubmit={handleForgotStep2}>
                  {/* Question 1 */}
                  <div className="bg-gray-700/50 rounded-lg p-3 mb-3">
                    <label className="text-teal-400 text-sm font-medium">Question 1:</label>
                    <p className="text-white text-sm mt-1 mb-2">"{forgotQuestion1}"</p>
                    <input
                      type="text"
                      value={forgotAnswer1}
                      onChange={(e) => setForgotAnswer1(e.target.value)}
                      placeholder="Your answer..."
                      className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm"
                    />
                  </div>
                  
                  <div className="text-center text-gray-500 text-sm mb-3">— OR —</div>
                  
                  {/* Question 2 */}
                  <div className="bg-gray-700/50 rounded-lg p-3 mb-3">
                    <label className="text-teal-400 text-sm font-medium">Question 2:</label>
                    <p className="text-white text-sm mt-1 mb-2">"{forgotQuestion2}"</p>
                    <input
                      type="text"
                      value={forgotAnswer2}
                      onChange={(e) => setForgotAnswer2(e.target.value)}
                      placeholder="Your answer..."
                      className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm"
                    />
                  </div>
                  
                  <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-2 mb-4">
                    <p className="text-yellow-300 text-xs text-center">
                      💡 Answers are NOT case-sensitive
                    </p>
                  </div>
                  
                  {/* New Password */}
                  <div className="mb-3">
                    <label className="text-gray-400 text-sm block mb-1">New Password</label>
                    <input
                      type="password"
                      value={forgotNewPassword}
                      onChange={(e) => setForgotNewPassword(e.target.value)}
                      placeholder="Minimum 6 characters"
                      className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="text-gray-400 text-sm block mb-1">Confirm New Password</label>
                    <input
                      type="password"
                      value={forgotConfirmPassword}
                      onChange={(e) => setForgotConfirmPassword(e.target.value)}
                      placeholder="Confirm password"
                      className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className={`w-full font-bold py-3 rounded-lg transition mb-3 ${
                      forgotLoading
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white'
                    }`}
                  >
                    {forgotLoading ? 'Verifying...' : '🔒 Reset Password'}
                  </button>
                </form>
                
                <button
                  onClick={() => setForgotStep(1)}
                  className="w-full text-gray-400 hover:text-white text-sm py-2"
                >
                  ← Back
                </button>
              </div>
            ) : (
              /* Step 3: Success */
              <div className="text-center">
                <div className="text-5xl mb-4">🎉</div>
                <h2 className="text-white text-xl font-bold mb-2">Password Reset!</h2>
                <p className="text-gray-400 mb-6">Your password has been successfully changed.</p>
                <button
                  onClick={resetForgotPassword}
                  className="w-full bg-gradient-to-r from-blue-500 to-teal-500 text-white font-bold py-3 rounded-lg"
                >
                  🎮 Login Now
                </button>
              </div>
            )}
          </div>
        ) : mode === 'register' && registerStep === 2 ? (
          /* 🆕 REGISTRATION STEP 2: Security Questions */
          <div>
            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-sm">✓</div>
              <div className="w-12 h-1 bg-teal-500 rounded"></div>
              <div className="w-8 h-8 rounded-full bg-teal-500 text-white flex items-center justify-center font-bold text-sm">2</div>
            </div>
            
            <div className="text-center mb-4">
              <div className="text-3xl mb-2">🔐</div>
              <h2 className="text-white text-lg font-bold">Security Questions</h2>
              <p className="text-gray-400 text-sm mt-1">Step 2 of 2: For password recovery</p>
            </div>
            
            {error && (
              <div className="bg-red-600/20 border border-red-500 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}
            
            <form onSubmit={handleRegisterStep2}>
              {/* Question 1 */}
              <div className="bg-gray-700/50 rounded-lg p-3 mb-3">
                <label className="text-teal-400 text-sm font-medium">Your Question 1:</label>
                <input
                  type="text"
                  value={securityQ1}
                  onChange={(e) => setSecurityQ1(e.target.value)}
                  placeholder="e.g. What was my first car?"
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg mt-2 text-sm"
                />
                <label className="text-gray-400 text-xs mt-2 block">Your Answer:</label>
                <input
                  type="text"
                  value={securityA1}
                  onChange={(e) => setSecurityA1(e.target.value)}
                  placeholder="e.g. Ford Fiesta"
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg mt-1 text-sm"
                />
              </div>
              
              {/* Question 2 */}
              <div className="bg-gray-700/50 rounded-lg p-3 mb-3">
                <label className="text-teal-400 text-sm font-medium">Your Question 2:</label>
                <input
                  type="text"
                  value={securityQ2}
                  onChange={(e) => setSecurityQ2(e.target.value)}
                  placeholder="e.g. My favourite teacher's name?"
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg mt-2 text-sm"
                />
                <label className="text-gray-400 text-xs mt-2 block">Your Answer:</label>
                <input
                  type="text"
                  value={securityA2}
                  onChange={(e) => setSecurityA2(e.target.value)}
                  placeholder="e.g. Mrs Jones"
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg mt-1 text-sm"
                />
              </div>
              
              <div className="bg-blue-500/20 border border-blue-500 rounded-lg p-2 mb-4">
                <p className="text-blue-300 text-xs text-center">
                  💡 Create questions only YOU can answer. These help recover your account if you forget your password.
                </p>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className={`w-full font-bold py-3 rounded-lg transition ${
                  loading
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white'
                }`}
              >
                {loading ? 'Creating Account...' : '✅ Complete Registration'}
              </button>
            </form>
            
            <button
              onClick={() => setRegisterStep(1)}
              className="w-full mt-3 text-gray-400 hover:text-white text-sm"
            >
              ← Back
            </button>
          </div>
        ) : (
          /* NORMAL LOGIN / REGISTER STEP 1 */
          <>
            {/* Toggle Login/Register */}
            <div className="flex bg-gray-700 rounded-lg p-1 mb-6">
              <button
                onClick={() => handleModeSwitch('login')}
                className={`flex-1 py-2 rounded-md text-sm font-bold transition ${
                  mode === 'login' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Login
              </button>
              <button
                onClick={() => handleModeSwitch('register')}
                className={`flex-1 py-2 rounded-md text-sm font-bold transition ${
                  mode === 'register' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Register
              </button>
            </div>
            
            {/* Progress indicator for register */}
            {mode === 'register' && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-teal-500 text-white flex items-center justify-center font-bold text-sm">1</div>
                <div className="w-12 h-1 bg-gray-600 rounded"></div>
                <div className="w-8 h-8 rounded-full bg-gray-600 text-gray-400 flex items-center justify-center font-bold text-sm">2</div>
              </div>
            )}
            
            {mode === 'register' && (
              <p className="text-gray-400 text-sm text-center mb-4">Step 1 of 2: Your Details</p>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-600/20 border border-red-500 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={mode === 'login' ? handleLoginSubmit : handleRegisterStep1}>
              {mode === 'register' && (
                <>
                  {/* Name Field */}
                  <div className="mb-4">
                    <label className="text-gray-400 text-sm block mb-1">Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your display name"
                      className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Phone Input */}
                  <div className="mb-4">
                    <PhoneInput
                      label="Phone Number (Optional)"
                      onChange={(data) => setPhoneData(data)}
                      defaultCountry="GB"
                      placeholder="Phone number"
                    />
                  </div>
                </>
              )}
              
              <div className="mb-4">
                <label className="text-gray-400 text-sm block mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="mb-2">
                <label className="text-gray-400 text-sm block mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'register' ? 'Minimum 6 characters' : 'Enter password'}
                  required
                  minLength={6}
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Forgot Password Link (only on login) */}
              {mode === 'login' && (
                <div className="mb-4 text-right">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-blue-400 hover:text-blue-300 text-sm"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
              
              {mode === 'register' && <div className="mb-4"></div>}
              
              <button
                type="submit"
                disabled={loading}
                className={`w-full font-bold py-3 rounded-lg transition ${
                  loading
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white'
                }`}
              >
                {loading ? 'Please wait...' : mode === 'login' ? 'Login' : 'Continue →'}
              </button>
            </form>

            {/* Skip for now */}
            <button
              onClick={() => onAuthSuccess(null)}
              className="w-full mt-4 text-gray-500 hover:text-gray-400 text-sm"
            >
              Continue as Guest
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthScreen;
